import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Phone, Check, ArrowRight, Clock, Truck, Shield } from 'lucide-react';
import { companyInfo } from '../data/mockData';
import { useTheme } from '../context/ThemeContext';
import SEO, { createServiceSchema } from '../components/SEO';

const towingServices = [
  "24/7 Emergency Towing",
  "Flatbed Towing",
  "Motorcycle Towing",
  "Long Distance Towing",
  "Accident Recovery",
  "Breakdown Assistance",
  "Vehicle Transportation",
  "Roadside Assistance"
];

const TowingPage = () => {
  const { isDark } = useTheme();

  const serviceSchema = createServiceSchema(
    '24/7 Towing Services',
    'Reliable 24/7 towing and roadside assistance in Murray Bridge and surrounding areas. Emergency towing, flatbed towing, accident recovery, and vehicle transportation.',
    'https://www.jprcustoms.com.au/towing'
  );

  return (
    <div className={`min-h-screen ${isDark ? 'bg-neutral-950' : 'bg-white'}`}>
      <SEO 
        title="24/7 Towing Services & Roadside Assistance"
        description="24/7 towing services in Murray Bridge SA. Emergency towing, flatbed towing, accident recovery, breakdown assistance, long distance towing. Fully insured. Call +61 417 119 544."
        keywords="towing Murray Bridge, 24/7 towing, emergency towing, roadside assistance, flatbed towing, accident recovery, breakdown service, tow truck"
        canonical="/towing"
        structuredData={serviceSchema}
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/fe401e87-303d-4ea8-9dff-a675f621b334/images/607cab205b27c121e9bc16606f8595a18e3f5c82cf6011bca205104b8177d0b6.png)` }}
          >
            <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-r from-neutral-950/95 via-neutral-950/80 to-neutral-950/60' : 'bg-gradient-to-r from-white/95 via-white/85 to-white/70'}`} />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 pt-44">
            <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-6 ${isDark ? 'text-white' : 'text-neutral-900'}`}>
              Professional<br />
              <span className="text-lime-500">Towing Services</span>
            </h1>
            <p className={`text-xl max-w-2xl mb-8 ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
              Reliable 24/7 towing and roadside assistance throughout Murray Bridge and surrounding areas.
            </p>
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center gap-2 px-8 py-4 bg-lime-500 text-black font-semibold hover:bg-lime-400 transition-all duration-300 rounded-lg"
            >
              <Phone className="w-5 h-5" />
              Call Now - 24/7
            </a>
          </div>
        </section>

        {/* Features */}
        <section className={`py-16 ${isDark ? 'bg-neutral-800' : 'bg-neutral-50'}`}>
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className={`p-8 rounded-xl text-center ${isDark ? 'bg-neutral-900' : 'bg-white shadow-lg'}`}>
                <div className="w-16 h-16 bg-lime-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-black" />
                </div>
                <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-neutral-900'}`}>24/7 Availability</h3>
                <p className={isDark ? 'text-neutral-400' : 'text-neutral-600'}>We're always here when you need us, day or night.</p>
              </div>
              <div className={`p-8 rounded-xl text-center ${isDark ? 'bg-neutral-900' : 'bg-white shadow-lg'}`}>
                <div className="w-16 h-16 bg-lime-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Truck className="w-8 h-8 text-black" />
                </div>
                <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-neutral-900'}`}>Modern Fleet</h3>
                <p className={isDark ? 'text-neutral-400' : 'text-neutral-600'}>Equipped with the latest towing equipment for safe transport.</p>
              </div>
              <div className={`p-8 rounded-xl text-center ${isDark ? 'bg-neutral-900' : 'bg-white shadow-lg'}`}>
                <div className="w-16 h-16 bg-lime-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-black" />
                </div>
                <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-neutral-900'}`}>Fully Insured</h3>
                <p className={isDark ? 'text-neutral-400' : 'text-neutral-600'}>Your vehicle is protected throughout the entire process.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className={`py-20 ${isDark ? 'bg-neutral-900' : 'bg-white'}`}>
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-lime-500 mb-6">Reliable Towing When You Need It</h2>
                <p className={`mb-6 leading-relaxed ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
                  Whether you've broken down on the highway, been in an accident, or simply need your vehicle transported, JPR Customs provides fast, reliable towing services.
                </p>
                <p className={`mb-8 leading-relaxed ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
                  Our experienced operators handle every vehicle with care, using the latest equipment to ensure safe transportation.
                </p>
                <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-neutral-900'}`}>Our Towing Services Include:</h3>
                <ul className="space-y-3">
                  {towingServices.map((service, index) => (
                    <li key={index} className={`flex items-center gap-3 ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
                      <Check className="w-5 h-5 text-lime-500 flex-shrink-0" />
                      <span>{service}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="space-y-4">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/fe401e87-303d-4ea8-9dff-a675f621b334/images/607cab205b27c121e9bc16606f8595a18e3f5c82cf6011bca205104b8177d0b6.png" 
                  alt="Tow truck service"
                  className="w-full h-64 object-cover rounded-xl"
                />
                <img 
                  src="https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=800&q=80" 
                  alt="Roadside assistance"
                  className="w-full h-64 object-cover rounded-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className={`py-16 ${isDark ? 'bg-neutral-800' : 'bg-neutral-50'}`}>
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className={`text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-neutral-900'}`}>Stranded? We Can Help!</h2>
            <p className={`mb-8 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>Call us anytime, day or night, for fast and reliable towing assistance.</p>
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center gap-2 px-8 py-4 bg-lime-500 text-black font-semibold hover:bg-lime-400 transition-all duration-300 rounded-lg"
            >
              Call {companyInfo.phone}
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default TowingPage;
